import React, { useState, useEffect, useRef } from "react";
import Draggable from "react-draggable";
import ReactTooltip from "react-tooltip";
import { useDispatch, useSelector } from "react-redux";

import "./BulkQaStatusUpdate.css";

import Portal from "../../common/components/Portal";
import projectActions from "../../project/actions";
import { notifyApiError, notifySuccess } from "../../common/notify";
import * as Service from "../services";
import { REVIEW_GATE_STATUS_CLASS, REVIEW_GATE_STATUS_LABELS } from "../utils/qaGateValidation";

const ROW_LIMIT = 200;

const groupForReason = reason => (typeof reason === "string" && reason.trim() ? reason.trim() : "Skipped");

const runBulkQaUpdate = async (ids, { dryRun = false } = {}) => {
    const unique = [...new Set((ids || []).filter(Boolean))];
    if (!unique.length) return null;
    const data = (await Service.bulkQaUpdate({ recommendation_ids: unique, ...(dryRun ? { dry_run: true } : {}) }))?.data || {};
    return {
        updated: data.updated || [],
        skipped: data.skipped || [],
        not_found: data.not_found || [],
        invalid_ids: data.invalid_ids || [],
        requested: unique.length
    };
};

const BulkQaStatusUpdate = ({ recommendationIds, projectId, viewOnly, onCompleted }) => {
    const [isQaQcReviewerUser, setIsQaQcReviewerUser] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const dispatch = useDispatch();
    const reviewers = useSelector(state => state.projectReducer.getReviewersByProjectData?.reviewer);

    useEffect(() => {
        if (localStorage.getItem("role") === "super_admin" || !projectId) return;
        dispatch(
            projectActions.getReviewersByProject({
                project_id: projectId,
                user_id: localStorage.getItem("userId") || "",
                is_active: true
            })
        );
    }, [projectId, dispatch]);

    useEffect(() => {
        const isSuperAdmin = localStorage.getItem("role") === "super_admin";
        setIsQaQcReviewerUser(isSuperAdmin || (Array.isArray(reviewers) && reviewers.length > 0));
    }, [reviewers]);

    if (!isQaQcReviewerUser) return null;

    const count = recommendationIds?.length || 0;

    return (
        <>
            <ReactTooltip id="bulk-qa-passed" className="rc-tooltip-custom-class" effect="solid" place="bottom" backgroundColor="#007bff" />
            <button
                type="button"
                className={`add-btn ml-2 btn-new-noti bulk-qa-passed-btn ${viewOnly ? "cursor-disabled" : ""}`}
                onClick={() => setIsOpen(true)}
                disabled={viewOnly || !count}
                data-for="bulk-qa-passed"
                data-delay-show="500"
                data-tip="Mark the selected recommendations as QA/QC Passed. Only those that are Ready for QA with all issues approved will change."
            >
                <i className="fas fa-clipboard-check mr-1" />
                Mark QA/QC Passed
            </button>
            {isOpen && (
                <Portal
                    body={
                        <BulkQaPassedModal
                            recommendationIds={recommendationIds}
                            onClose={() => setIsOpen(false)}
                            onCompleted={onCompleted}
                        />
                    }
                    onCancel={() => setIsOpen(false)}
                />
            )}
        </>
    );
};

const OutcomeRow = ({ title, rows, onView }) => (
    <div className="bulk-qa__row">
        <i className="fas fa-minus-circle bulk-qa__row-icon" />
        <span className="bulk-qa__row-count">{rows.length}</span>
        <span className="bulk-qa__row-title">{title}</span>
        <button type="button" className="bulk-qa__row-view" onClick={() => onView({ title, rows })}>
            View
        </button>
    </div>
);

const OutcomeListModal = ({ title, rows, onBack }) => (
    <div className="modal modal-region bulk-qa-modal bulk-qa-list-modal" tabIndex="-1" style={{ display: "block" }} role="dialog">
        <Draggable handle=".draggable" cancel=".close, button, .bulk-qa__table-wrap">
            <div className="modal-dialog" role="document">
                <div className="modal-content">
                    <div className="modal-header draggable">
                        <h5 className="modal-title">
                            <div className="txt-hed">
                                {title} <span className="bulk-qa__list-count">{rows.length}</span>
                            </div>
                        </h5>
                        <button type="button" className="close close-icn-sec" onClick={onBack}>
                            <span aria-hidden="true">
                                <img src="/img/close.svg" alt="" />
                            </span>
                        </button>
                    </div>
                    <div className="modal-body bulk-qa__list-body">
                        <div className="bulk-qa__table-wrap">
                            <table className="bulk-qa__table">
                                <thead>
                                    <tr>
                                        <th>Code</th>
                                        <th>Current status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.slice(0, ROW_LIMIT).map(row => (
                                        <tr key={row.id}>
                                            <td className="bulk-qa__table-code">{row.code || row.id}</td>
                                            <td>
                                                <StatusPill status={row.status} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {rows.length > ROW_LIMIT ? (
                            <div className="bulk-qa__table-more">Showing the first {ROW_LIMIT} of {rows.length}.</div>
                        ) : null}
                    </div>
                    <div className="modal-footer bulk-qa__footer">
                        <button type="button" className="btn btn-primary btnRgion" onClick={onBack}>
                            Back
                        </button>
                    </div>
                </div>
            </div>
        </Draggable>
    </div>
);

const StatusPill = ({ status }) => {
    if (!status) return <span className="bulk-qa__muted">—</span>;
    return <span className={`bulk-qa__pill ${REVIEW_GATE_STATUS_CLASS[status] || ""}`}>{REVIEW_GATE_STATUS_LABELS[status] || status}</span>;
};

const BulkQaPassedModal = ({ recommendationIds, onClose, onCompleted }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [preview, setPreview] = useState(null);
    const [viewing, setViewing] = useState(null);
    const isMountedRef = useRef(true);

    useEffect(() => () => (isMountedRef.current = false), []);

    const count = recommendationIds?.length || 0;

    useEffect(() => {
        let cancelled = false;
        const loadPreview = async () => {
            setIsLoading(true);
            try {
                const response = await runBulkQaUpdate(recommendationIds, { dryRun: true });
                if (cancelled || !isMountedRef.current) return;
                setPreview(response);
            } catch (error) {
                if (cancelled || !isMountedRef.current) return;
                notifyApiError(error);
                onClose();
            } finally {
                !cancelled && isMountedRef.current && setIsLoading(false);
            }
        };
        loadPreview();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRun = async () => {
        setIsLoading(true);
        try {
            const response = await runBulkQaUpdate(recommendationIds);
            if (!isMountedRef.current) return;
            const passed = response?.updated?.length || 0;
            notifySuccess(`${passed} recommendation${passed === 1 ? "" : "s"} marked as QA/QC Passed`);
            onCompleted && onCompleted();
            onClose();
        } catch (error) {
            if (!isMountedRef.current) return;
            notifyApiError(error);
            onClose();
        } finally {
            isMountedRef.current && setIsLoading(false);
        }
    };

    const skippedRows = preview
        ? [
              ...preview.skipped.map(item => ({
                  id: item?.recommendation_id,
                  code: item?.recommendation_code,
                  status: item?.current_status,
                  title: groupForReason(item?.reason)
              })),
              ...preview.not_found.map(item => ({
                  id: item?.recommendation_id ?? item,
                  code: item?.recommendation_code,
                  status: null,
                  title: "Not found or not accessible"
              })),
              ...preview.invalid_ids.map(item => ({
                  id: item?.recommendation_id ?? item,
                  code: item?.recommendation_code,
                  status: null,
                  title: "Invalid ID"
              }))
          ]
        : [];

    const groupedSkips = skippedRows.reduce((acc, row) => {
        if (!acc[row.title]) acc[row.title] = { title: row.title, rows: [] };
        acc[row.title].rows.push(row);
        return acc;
    }, {});

    const willPassCount = preview?.updated?.length || 0;

    if (viewing) {
        return <OutcomeListModal title={viewing.title} rows={viewing.rows} onBack={() => setViewing(null)} />;
    }

    return (
        <div className="modal modal-region bulk-qa-modal" tabIndex="-1" style={{ display: "block" }} role="dialog">
            <Draggable handle=".draggable" cancel=".close, button, .bulk-qa__sections">
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header draggable">
                            <h5 className="modal-title">
                                <div className="txt-hed">Mark as QA/QC Passed</div>
                            </h5>
                            <button type="button" className="close close-icn-sec" onClick={onClose} disabled={isLoading}>
                                <span aria-hidden="true">
                                    <img src="/img/close.svg" alt="" />
                                </span>
                            </button>
                        </div>

                        <div className="modal-body bulk-qa__body">
                            {!preview ? (
                                <div className="bulk-qa__checking">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                    <p>
                                        Checking {count} recommendation{count === 1 ? "" : "s"}…
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="bulk-qa__stats">
                                        <div className={`bulk-qa__stat ${willPassCount ? "bulk-qa__stat--ok" : ""}`}>
                                            <span className="bulk-qa__stat-num">{willPassCount}</span>
                                            <span className="bulk-qa__stat-lbl">Will pass</span>
                                        </div>
                                        <div className={`bulk-qa__stat ${skippedRows.length ? "bulk-qa__stat--warn" : ""}`}>
                                            <span className="bulk-qa__stat-num">{skippedRows.length}</span>
                                            <span className="bulk-qa__stat-lbl">Will skip</span>
                                        </div>
                                        <div className="bulk-qa__stat">
                                            <span className="bulk-qa__stat-num">{preview.requested}</span>
                                            <span className="bulk-qa__stat-lbl">Selected</span>
                                        </div>
                                    </div>

                                    {!willPassCount ? (
                                        <div className="bulk-qa__none">
                                            None of the selected recommendations can be passed yet. Use View below to see what is blocking them.
                                        </div>
                                    ) : null}

                                    {skippedRows.length ? (
                                        <>
                                            <div className="bulk-qa__subhead">Skipped reasons</div>
                                            <div className="bulk-qa__sections">
                                                {Object.values(groupedSkips).map(group => (
                                                    <OutcomeRow
                                                        key={group.title}
                                                        title={group.title}
                                                        rows={group.rows}
                                                        onView={setViewing}
                                                    />
                                                ))}
                                            </div>
                                        </>
                                    ) : null}

                                    {willPassCount ? (
                                        <div className="bulk-qa__warning">
                                            <i className="fas fa-exclamation-triangle mr-2" />
                                            <span>
                                                This cannot be undone in bulk. To reverse it you would have to open each recommendation and change
                                                its QA status back one at a time.
                                            </span>
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </div>

                        <div className="modal-footer bulk-qa__footer">
                            <button type="button" className="btn btn-secondary btnClr" onClick={onClose} disabled={isLoading}>
                                Cancel
                            </button>
                            {preview ? (
                                <button
                                    type="button"
                                    className="btn btn-primary btnRgion"
                                    onClick={handleRun}
                                    disabled={isLoading || !willPassCount}
                                >
                                    {isLoading ? (
                                        <span className="d-flex align-items-center justify-content-center">
                                            <span className="spinner-border spinner-border-sm mr-2" role="status" />
                                            Applying…
                                        </span>
                                    ) : willPassCount ? (
                                        `Pass ${willPassCount} recommendation${willPassCount === 1 ? "" : "s"}`
                                    ) : (
                                        "Mark QA/QC Passed"
                                    )}
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>
            </Draggable>
        </div>
    );
};

export default BulkQaStatusUpdate;
